<template>
  <div class="ui-phone-input inline-flex items-center" :class="{ 'ui-phone-input--disabled': disabled }">
    <UiDropdown :disabled="disabled" class="w-20" :class="{'pointer-events-none': disabled }">
      <template #default="{ open }">
        <div 
          class="dropdown-toggle box-border flex h-[46px] w-max items-center justify-center rounded-l-lg pl-2 pr-1 hover:cursor-pointer hover:bg-transparent"
        >
          <img :src="country.flag" alt="Selected country flag" width="18" height="18" />
          <UiIcon icon="caret-down" size="18" class="transition-transform duration-300" :class="{ '-rotate-180': open }"/>
          <span class="whitespace-nowrap text-sm">{{ country.prefix }}</span>
        </div>
      </template>
      <template #content>
        <div class="dropdown-content inline-flex w-48 flex-col">
          <div v-for="(item, i) of countries" :key="i" class="dropdown-item" @click="country = item">
            <img :src="item.flag" alt="Country flag" width="18" height="18" />
            <span class="">{{ item.name }}</span>
            <span class="text-slate-400">{{ item.prefix }}</span>
          </div>
        </div>
      </template>
    </UiDropdown>
    <UiInput
      v-model="value"
      type="text"
      :mask="JSON.stringify(country.mask || '')"
      :placeholder="placeholder ?? country.placeholder"
      :disabled="disabled"
      class="ui-phone-input-content w-full hover:bg-transparent focus:border-transparent focus:bg-transparent border-0 disabled:bg-transparent"
    ></UiInput>
  </div>
</template>
<script setup lang="ts">
import { ref, computed } from 'vue'
import UiDropdown from '@/components/ui/dropdown.vue'
import UiIcon from '@/components/ui/icon.vue'
import UiInput from '@/components/ui/input.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  modelValue?: string | number | null
  disabled?: boolean
  placeholder?: string
}>()

const emit = defineEmits(['update:modelValue'])

const countries = [
  {
    code: 'BR',
    name: 'Brazil',
    prefix: '+55',
    flag: 'https://cdn.kcak11.com/CountryFlags/countries/br.svg',
    mask: ['(##) ####-####', '(##) #####-####'],
    placeholder: `(DD) + ${t('labels.number')}`
  },
  {
    code: 'PY',
    name: 'Paraguay',
    prefix: '+595',
    flag: 'https://cdn.kcak11.com/CountryFlags/countries/py.svg',
    mask: ['(##) ###-###', '(###) ###-###', '(##) #####-####', '(###) #####-####'],
    placeholder: `(DD) + ${t('labels.number')}`
  }
]

const country = ref({ ...countries[0] })

const setByCountry = (countryCode: string) => {
  const result = countries.find((el) => el.code === countryCode)
  if (result?.code) country.value = result
}

const setByPrefix = (prefix: string) => {
  const index = countries.findIndex((el) => el.prefix === prefix)
  if (index !== -1) country.value = countries[index]
}

const value = computed({
  get: () => {
    const sortedPrefixes = countries.map((el) => el.prefix).sort((a, b) => b.length - a.length)

    if (props.modelValue && typeof props.modelValue === 'string') {
      const prefix = sortedPrefixes.find((pref) => pref === `${props.modelValue}`.substring(0, pref.length))
      if (prefix) {
        setByPrefix(prefix)
        return `${props.modelValue}`.split(prefix).pop()
      }
    }
    return props.modelValue
  },
  set: (val) => {
    if(val === '') return emit('update:modelValue', null)

    const clean = `${val}`.replace(/\D/g, '')
    return emit('update:modelValue', `${country.value.prefix}${clean}`)
  }
})

defineExpose({
  setByCountry,
  setByPrefix
})
</script>

<style scoped lang="scss">
.ui-phone-input {
  @apply box-border rounded-lg border-solid transition-all dark:bg-[#2C2C2C] dark:text-[#FDFDFD] border border-[#bbb];

  &:hover {
    @apply bg-[#f8f6fb] dark:bg-[#343434];
  }

  &:focus-within {
    @apply border-[--c-primary-600];
  }

  &--disabled {
    @apply bg-[#e6e1e6] dark:bg-[#414141] hover:bg-[#e6e1e6] hover:dark:bg-[#414141] text-[#79767A];
  }
}

.ui-phone-input-content {
  @apply box-border border-0 bg-transparent py-[14px] pl-2 pr-4 text-sm font-normal leading-4 outline-none;
}

.dropdown-item {
  @apply box-border flex w-full items-center justify-start gap-4 p-2 text-sm hover:cursor-pointer hover:bg-slate-100 dark:hover:bg-neutral-700 text-[#2C2C2C];
}
</style>
